// components/models/Flux3ControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import { Flux3AspectRatio, Flux3Params, Flux3Resolution } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface Flux3ControlPanelProps {
  params: Flux3Params;
  setModelParams: (params: (prevParams: { [model: string]: Flux3Params }) => { [model: string]: Flux3Params }) => void;
  diffusionModel: string;
}

const defaultFlux3Params: Flux3Params = {
  prompt: '',
  duration: 5,
  resolution: '720p',
  aspect_ratio: 'auto',
  generate_audio: false,
  seed: -1,
};

export const getDefaultFlux3Params = (): Flux3Params => ({ ...defaultFlux3Params });

// The draft tier is 720p by construction, so it exposes no resolution.
export const getDefaultFlux3DraftParams = (): Flux3Params => ({ ...defaultFlux3Params });

const RESOLUTION_OPTIONS: Flux3Resolution[] = ['720p', '1080p'];
const ASPECT_RATIO_OPTIONS: Flux3AspectRatio[] = ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'];

const Flux3ControlPanel: React.FC<Flux3ControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();
  const isDraft = diffusionModel === 'flux-3-draft';

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Flux3ControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: (newValue as string).length } : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'Flux3ControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Flux3ControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Flux3ControlPanel',
    });
  };

  const onSwitchGenerateAudio = () => {
    const newValue = !params.generate_audio;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Flux3ControlPanel] onSwitchGenerateAudio: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, generate_audio: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Flux3ControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={5}
          max={20}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description="In seconds."
        />
      </div>

      {!isDraft && (
        <div className="mb-4">
          <Select
            id="flux-3-resolution"
            label="Resolution"
            value={params.resolution}
            options={RESOLUTION_OPTIONS}
            onChange={(value) => handleSelectChange('resolution', value)}
          />
        </div>
      )}

      <div className="mb-4">
        <Select
          id={`${isDraft ? 'flux-3-draft' : 'flux-3'}-aspect-ratio`}
          label="Aspect Ratio"
          value={params.aspect_ratio ?? 'auto'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio === true}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>

      {isDraft && (
        <p className="mb-4 text-xs text-[#777777]">Draft tier: 720p only.</p>
      )}
    </>
  );
});

Flux3ControlPanel.displayName = 'Flux3ControlPanel';

export default Flux3ControlPanel;
